import { PropsWithChildren, RefObject } from 'react';
import { GridColDefExtended, GridFilterModel, GridLogicOperator, GridSortDirection, GridSortModel } from '@mui/x-data-grid-pro';
import { getEnumEntryByString, getObjectEntryByKey } from '@/Infrastructure/Utilities';
import { BoardQuery } from '@api/Trinium/types';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { dispatchColumnDefinitions } from '@components/DataGrid/Dispatch/columns';
import { FilterMap } from '../../Filters/types';
import { EditableCellRef, EditSaveScope } from '../types';

/** Available columns for Dispatch Leg entity */
export enum DispatchLegColumns {
  actionMenu = 'actionMenu',
  __check__ = '__check__',
  orderNumber = 'orderNumber',
  leg = 'leg',
  container = 'container',
  containerProvider = 'containerProvider',
  chassis = 'chassis',
  driver = 'driver',
  pickup = 'pickup',
  puSealNo = 'puSealNo',
  dehire = 'dehire',
  deliver = 'deliver',
  deliverDate = 'deliverDate',
  dropDate = 'dropDate',
  customer = 'customer',
  scheduledPickup = 'scheduledPickup',
  scheduledDelivery = 'scheduledDelivery',
  bookingBol = 'bookingBol',
  releaseNo = 'releaseNo',
  lastFreeDay = 'lastFreeDay',
  available = 'available',
  jobDate = 'jobDate',
  pickupTime = 'pickupTime',
  vessel = 'vessel',
  notes = 'notes',
  csnNotes = 'csnNotes',
  railBilling = 'railBilling',
  trcCntrAvail = 'trcCntrAvail',
  trcCntrHolds = 'trcCntrHolds',
  trcCntrStatus = 'trcCntrStatus',
  trcDestInfo = 'trcDestInfo',
  trcETADate = 'trcETADate',
  trcEvent = 'trcEvent',
  trcEventState = 'trcEventState',
  trcInvStatus = 'trcInvStatus',
  trcLFD = 'trcLFD',
  trcResult = 'trcResult'
}

/** Explicit rendering definitions for the available dispatch leg columns  */
export type DispatchColumnDefinitions = Record<DispatchLegColumns, GridColDefExtended<DispatchLeg>>;

export interface DispatchFilterMap {
  AvailableDate: FilterMap<DispatchLeg>;
  Chassis: FilterMap<DispatchLeg>;
  Customer: FilterMap<DispatchLeg>;
  Cutoff: FilterMap<DispatchLeg>;
  Delivery: FilterMap<DispatchLeg>;
  Driver: FilterMap<DispatchLeg>;
  DriverPriority: FilterMap<DispatchLeg>;
  Hazmat: FilterMap<DispatchLeg>;
  JobCategory: FilterMap<DispatchLeg>;
  LastFreeDay: FilterMap<DispatchLeg>;
  Leg: FilterMap<DispatchLeg>;
  LegPriority: FilterMap<DispatchLeg>;
  LegStatus: FilterMap<DispatchLeg>;
  LegType: FilterMap<DispatchLeg>;
  PickupDate: FilterMap<DispatchLeg>;
  Pickup: FilterMap<DispatchLeg>;
  PickupDelivery: FilterMap<DispatchLeg>;
  ScheduledDeliveryDate: FilterMap<DispatchLeg>;
  ScheduledPickupDate: FilterMap<DispatchLeg>;
  ScheduledPickupDeliveryDate: FilterMap<DispatchLeg>;
  TransportType: FilterMap<DispatchLeg>;
}

export const defaultFilterModel: GridFilterModel = {
  quickFilterExcludeHiddenColumns: true,
  quickFilterLogicOperator: GridLogicOperator.And,
  items: []
};

export interface InsertHubProps {
  onClose: () => void;
}

export interface ExportActionProps {
  name: string;
  disabled?: boolean;
}

export interface StatusActionProps {
  leg: DispatchLeg;
  onError?: (error: string) => void;
  selectedRows?: DispatchLeg[];
}

export interface ActionItemListProps {
  onClose: () => void;
}

/** Appends the leg count to a batch action/edit label so the user can see how many legs the change will touch */
export const batchLegLabel = (label: string, legCount: number) => (legCount > 1 ? `${label} (${String(legCount)} legs)` : label);

/**
 * Converts a Board Query's Sort Order to a Grid Sort Model.
 * Verifies Sort Column(s) exist and are included in the
 * Dispatch default columns or Query's Column configuration
 */
export const convertQuerySortOrderToGridSortModel = (query: BoardQuery): GridSortModel => {
  const items = query.configuration.sortOrder?.map((sort) => {
    // Convert the string to an enum, ensuring valid column name was received
    const validColumn = getEnumEntryByString(DispatchLegColumns, sort.column);
    if (!validColumn) {
      console.log(`Invalid sort column not found: ${sort.column}`);
      return;
    }

    // Find the corresponding Column Definition
    const colDetails = getObjectEntryByKey(dispatchColumnDefinitions, validColumn);
    if (!colDetails) {
      console.log(`Invalid sort column not found: ${sort.column}`);
      return;
    }

    const queryColumns = query.configuration.columns.map((c) => c);

    if (
      !queryColumns.includes(sort.column)
      // && !defaultColumns.includes(sort.column)
    ) {
      console.log(`Board Query ${query.name} (${query.id.toString()}) sort column not included in columns: ${sort.column}`);
    }

    return {
      field: colDetails.field,
      sort: (sort.isDesc ? 'desc' : 'asc') as GridSortDirection
    };
  });

  return items ? items.filter((item) => item !== undefined) : [];
};

export interface EditableCellForwardedProps {
  onClose?: () => void;
  /** Lets an edit cell report its form validity so the container can disable saving and keep the popover open */
  onValidityChange?: (isValid: boolean) => void;
}

export interface EditableContainerProps extends PropsWithChildren, EditableCellForwardedProps {
  editableRef: RefObject<EditableCellRef | null>;
  /** Number of grid rows selected when the editor opened, shown in the header and offered as the batch save target */
  selectedRowCount?: number;
}

export interface EditableCell<T> extends EditableCellForwardedProps {
  row: T;
  selectedRows?: T[];
}

/**
 * Rows an editable cell writes to: only the clicked row for a single save, or every eligible selected row
 * for a batch save. The clicked row is always selected by the time the editor opens, so it is part of the batch.
 */
export function resolveEditTargetRows<T extends { legId: number }>(
  scope: EditSaveScope,
  row: T,
  selectedRows: T[] | undefined,
  isEligible: (row: T) => boolean = () => true
): T[] {
  if (scope !== 'selection') {
    return [row];
  }

  return (selectedRows?.length ? selectedRows : [row]).filter(isEligible);
}
